/**
 * Scheduling delegates (migrations 2026-11-05-1780..1782, owner-approved 2026-11-05).
 *
 * An admin names an active MSR / Territory Manager / Regional Manager as the "scheduling delegate" of a sales position.
 * The delegate gets the position holder's SCHEDULING rights for that position's team (the position and everyone under
 * it): team availability, the team's events on the Sales Calendar, Confirm Schedule / Assign Rep, and the Lead rep.
 * Nothing else. The database enforces every rule; these are thin RPC wrappers.
 */
import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

export interface MySchedulingDelegation {
  delegation_id: UUID;
  position_id: UUID;
  position_title: string;
  holder_name: string | null;
  /** The delegated position and every active position under it. */
  team_position_ids: UUID[];
}

export interface SchedulingDelegate {
  id: UUID;
  delegate_profile_id: UUID;
  delegate_name: string;
  delegate_email: string | null;
  delegate_role: string;
  delegate_active: boolean;
  granted_by_name: string | null;
  granted_at: string;
  note: string | null;
  /** false while the delegate is inactive / no longer a field role, or the position is inactive (suspended). */
  is_effective: boolean;
}

export interface SchedulingDelegateCandidate {
  id: UUID;
  full_name: string;
  email: string | null;
  role: string;
}

/** The signed-in user's own delegations (empty for nearly everyone). */
export async function getMySchedulingDelegations(): Promise<
  MySchedulingDelegation[]
> {
  const { data, error } = await supabase.rpc("my_scheduling_delegations");
  if (error) {
    throw error;
  }
  return ((data ?? []) as MySchedulingDelegation[]).map((d) => ({
    ...d,
    team_position_ids: d.team_position_ids ?? [],
  }));
}

/** The positions the caller may staff on this event as a delegate (server rule, per delegated team; empty otherwise). */
export async function getMySchedulingPositionsForEvent(
  eventId: UUID,
): Promise<UUID[]> {
  const { data, error } = await supabase.rpc(
    "my_scheduling_positions_for_event",
    {
      p_event_id: eventId,
    },
  );
  if (error) {
    throw error;
  }
  return (data ?? []) as UUID[];
}

/** Active delegations of a position (Sales Team position page). Team readers only. */
export async function listSchedulingDelegates(
  positionId: UUID,
): Promise<SchedulingDelegate[]> {
  const { data, error } = await supabase.rpc("list_scheduling_delegates", {
    p_position_id: positionId,
  });
  if (error) {
    throw error;
  }
  return (data ?? []) as SchedulingDelegate[];
}

/** Admin picker: active MSR / TM / RM profiles matching the search (2+ characters), at most 20. */
export async function searchSchedulingDelegateCandidates(
  search: string,
): Promise<SchedulingDelegateCandidate[]> {
  const { data, error } = await supabase.rpc(
    "list_scheduling_delegate_candidates",
    {
      p_search: search,
    },
  );
  if (error) {
    throw error;
  }
  return (data ?? []) as SchedulingDelegateCandidate[];
}

export async function grantSchedulingDelegate(
  positionId: UUID,
  delegateProfileId: UUID,
  note?: string,
): Promise<UUID> {
  const { data, error } = await supabase.rpc("grant_scheduling_delegate", {
    p_position_id: positionId,
    p_delegate_profile_id: delegateProfileId,
    p_note: note?.trim() || null,
  });
  if (error) {
    throw new Error(describeSchedulingDelegateError(error));
  }
  return data as UUID;
}

export async function revokeSchedulingDelegate(
  delegationId: UUID,
  reason?: string,
): Promise<boolean> {
  const { data, error } = await supabase.rpc("revoke_scheduling_delegate", {
    p_delegation_id: delegationId,
    p_reason: reason?.trim() || null,
  });
  if (error) {
    throw new Error(describeSchedulingDelegateError(error));
  }
  return data === true;
}

const MESSAGES: Record<string, string> = {
  SCHED_DELEGATE_ADMIN_ONLY:
    "Only an admin can name or remove a scheduling delegate.",
  SCHED_DELEGATE_ROLE_NOT_ELIGIBLE:
    "The scheduling delegate must be an active MSR, Territory Manager or Regional Manager.",
  SCHED_DELEGATE_IS_HOLDER: "This person already holds the position.",
  SCHED_DELEGATE_ALREADY_GRANTED:
    "This person is already the scheduling delegate for this position.",
  SCHED_DELEGATE_POSITION_INACTIVE: "This position is inactive.",
};

export function describeSchedulingDelegateError(e: unknown): string {
  const msg = (e as { message?: string } | null)?.message ?? "";
  const code = /^([A-Z][A-Z0-9_]{3,}):/.exec(msg)?.[1];
  if (code && MESSAGES[code]) {
    return MESSAGES[code];
  }
  if (/lock|timeout|55P03/i.test(msg)) {
    return "Someone is saving a schedule for this team right now. Try again in a moment.";
  }
  return msg || "That change could not be saved.";
}
